import type { ImageLike, Page, Paragraph } from "tesseract.js";
// Self-hosted rather than left to tesseract.js's jsDelivr default, so the script
// this origin spawns a Web Worker from is served by this origin, is precached by
// the service worker, and can never drift from the installed tesseract.js.
import { isNative, recognizeNativeImage } from "./platform";

/**
 * Where the WebAssembly core and the language data are fetched from on the first
 * scan. Unset, tesseract.js uses its own version-pinned jsDelivr URLs, which is
 * what the deployed app does — shipping every SIMD variant of the core would add
 * tens of megabytes to `dist/`, and the library's feature detection picks the
 * right one. Point `VITE_OCR_ASSETS` at a base URL holding `core/` (a copy of
 * `tesseract.js-core`) and `lang/` (the `.traineddata.gz` files) to self-host.
 */
const OCR_ASSETS = import.meta.env?.VITE_OCR_ASSETS;
const NATIVE_BUILD = typeof __NATIVE_BUILD__ !== "undefined" && __NATIVE_BUILD__;

/** Reports recognition progress as a fraction from 0 to 1. */
export type OcrProgress = (fraction: number) => void;

export interface OcrOptions {
  onProgress?: OcrProgress;
}

/** Thrown when an image cannot be recognised, or contains no usable text. */
export class OcrError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "OcrError";
  }
}

const OFFLINE_MESSAGE =
  "You're offline. The text recognizer has to download once before it can work offline.";

/**
 * Tesseract's LSTM engine wants an x-height of roughly 20 pixels and gains
 * nothing above it, so both bounds matter: a 4000px phone photo is several times
 * slower than a 2600px one at the same accuracy, and a 600px screenshot of body
 * text is below the engine's floor until it is scaled up.
 */
const MAX_EDGE = 2600;
const MIN_EDGE = 1200;

/** Upscaling invents no detail, so past 2x it only costs time. */
const MAX_UPSCALE = 2;

/** How much to scale an image of this size before handing it to the engine. */
export const scaleFor = (width: number, height: number): number => {
  const longest = Math.max(width, height);
  if (longest === 0) return 1;
  if (longest > MAX_EDGE) return MAX_EDGE / longest;
  if (longest < MIN_EDGE) return Math.min(MAX_UPSCALE, MIN_EDGE / longest);
  return 1;
};

/**
 * Normalises an image for recognition: upright, and scaled into the band the
 * engine reads best.
 *
 * Falls back to handing the blob straight to tesseract.js whenever the browser
 * cannot decode it here — recognition still works, it just skips the rotation
 * and resizing. No greyscale or contrast pass: Tesseract binarises internally,
 * and a JS pixel loop over a 2600px image is slow enough on a phone to be felt.
 */
const prepareImage = async (image: Blob): Promise<ImageLike> => {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") {
    return image;
  }

  let bitmap: ImageBitmap;
  try {
    // Phone cameras record rotation in EXIF rather than rotating the pixels, so
    // a photo taken in portrait arrives sideways unless this is asked for.
    bitmap = await createImageBitmap(image, { imageOrientation: "from-image" });
  } catch {
    try {
      bitmap = await createImageBitmap(image);
    } catch (caught) {
      throw new OcrError("That file isn't an image this browser can read.", caught);
    }
  }

  try {
    const scale = scaleFor(bitmap.width, bitmap.height);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));

    const context = canvas.getContext("2d");
    if (!context) return image;

    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    bitmap.close();
  }
};

/**
 * Lines the engine is this unsure of are almost always page edges, gutter
 * shadows or the reader's thumb rather than text. The floor is deliberately low:
 * a stray word is one click to erase, whereas a dropped line is gone for good.
 */
const MIN_LINE_CONFIDENCE = 40;

/**
 * A word the page layout cut in half, as `informa- tion`.
 *
 * Where the break lands is up to the engine: scanning a rendered page, it folded
 * a heading broken across two printed rows into a single line, so the halves are
 * not always on either side of a line boundary. A hyphen with no space before it
 * and a lowercase letter after it is a cut word — a dash used as punctuation has
 * space on both sides. Hyphen, soft hyphen and the two Unicode hyphens, since
 * the engine picks between them by shape.
 */
const BROKEN_WORD = /(\p{L})[-\u00AD\u2010\u2011] +(\p{Ll})/gu;
const TRAILING_HYPHEN = /\p{L}[-\u00AD\u2010\u2011]$/u;

const repairBrokenWords = (text: string): string => text.replace(BROKEN_WORD, "$1$2");

/** Adds the next run of text, reattaching a word broken across the join. */
const append = (text: string, next: string): string =>
  TRAILING_HYPHEN.test(text) ? `${text.slice(0, -1)}${next}` : `${text} ${next}`;

/**
 * Rejoins the lines of one paragraph into running prose.
 *
 * The engine reports a page as it is laid out — one entry per printed line —
 * but an erasure is built from paragraphs, and `textToPage` splits on blank
 * lines. Keeping the printed line breaks would turn every column of a
 * newspaper into a stack of fragments.
 */
const paragraphText = (paragraph: Paragraph): string => {
  let text = "";

  for (const line of paragraph.lines) {
    if (line.confidence < MIN_LINE_CONFIDENCE) continue;

    const cleaned = repairBrokenWords(line.text.replace(/\s+/g, " ").trim());
    if (cleaned.length === 0) continue;

    text = text.length === 0 ? cleaned : append(text, cleaned);
  }

  return text;
};

/**
 * Turns a recognised page into text `articleFromText` can build an erasure from.
 *
 * Falls back to the engine's own flat text when structured blocks are missing,
 * which happens when the page is too sparse to lay out.
 */
export const pageToText = (page: Page): string => {
  const paragraphs: string[] = [];

  for (const block of page.blocks ?? []) {
    for (const paragraph of block.paragraphs) {
      const text = paragraphText(paragraph);
      if (text.length === 0) continue;

      // The break can also fall between paragraphs, where a column ends or a
      // heading gets a block of its own. In print a paragraph never ends in a
      // hyphen unless a word was cut short, so the same repair is safe here.
      const previous = paragraphs[paragraphs.length - 1];
      if (previous !== undefined && TRAILING_HYPHEN.test(previous)) {
        paragraphs[paragraphs.length - 1] = append(previous, text);
        continue;
      }

      paragraphs.push(text);
    }
  }

  return paragraphs.length > 0 ? paragraphs.join("\n\n") : page.text.trim();
};

/**
 * Setup is reported separately from recognition because it dominates the first
 * scan (a few megabytes of engine and language data) and all but disappears on
 * later ones, once both are cached.
 */
const SETUP_SHARE = 0.15;

/**
 * Reads the text out of an image, entirely in the browser.
 *
 * The worker is created and torn down per call rather than kept alive: it holds
 * tens of megabytes of WebAssembly heap, readers scan once and then erase for a
 * long while, and a second scan re-initialises from cache without touching the
 * network.
 */
export const recognizeImage = async (
  image: Blob,
  { onProgress }: OcrOptions = {},
): Promise<string> => {
  if (NATIVE_BUILD || isNative()) {
    try {
      const text = repairBrokenWords(await recognizeNativeImage(image));
      if (!/\p{L}/u.test(text))
        throw new OcrError("Couldn't find any text in that image.");
      return text;
    } catch (caught) {
      if (caught instanceof OcrError) throw caught;
      throw new OcrError("Couldn't read that image. Try a sharper photo.", caught);
    }
  }

  const prepared = await prepareImage(image);
  const [{ createWorker }, { default: workerScriptUrl }] = await Promise.all([
    import("tesseract.js"),
    import("tesseract.js/dist/worker.min.js?url"),
  ]);

  let worker: Awaited<ReturnType<typeof createWorker>>;
  try {
    worker = await createWorker("eng", undefined, {
      workerPath: workerScriptUrl,
      ...(OCR_ASSETS
        ? { corePath: `${OCR_ASSETS}/core`, langPath: `${OCR_ASSETS}/lang` }
        : {}),
      logger: ({ status, progress }) => {
        if (!onProgress) return;
        onProgress(
          status === "recognizing text"
            ? SETUP_SHARE + (1 - SETUP_SHARE) * progress
            : SETUP_SHARE * progress,
        );
      },
    });
  } catch (caught) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new OcrError(OFFLINE_MESSAGE, caught);
    }
    throw new OcrError("Couldn't start the text recognizer. Please try again.", caught);
  }

  try {
    // Without this Tesseract assumes 70 dpi and reads the page as far coarser
    // than it is. `prepareImage` normalises the long edge to about the width of
    // a printed page at 300 dpi, so this is close to the truth.
    await worker.setParameters({ user_defined_dpi: "300" });

    // rotateAuto deskews from the detected baselines, which matters for a photo
    // of a page held in one hand; it does not need the legacy OSD model.
    const { data } = await worker.recognize(
      prepared,
      { rotateAuto: true },
      { text: true, blocks: true },
    );

    const text = pageToText(data);
    if (!/\p{L}/u.test(text)) {
      throw new OcrError("Couldn't find any text in that image.");
    }
    return text;
  } catch (caught) {
    if (caught instanceof OcrError) throw caught;
    throw new OcrError("Couldn't read that image. Try a sharper photo.", caught);
  } finally {
    await worker.terminate();
  }
};
